// src/lib/servicesApi.js
import { db, auth } from "../firebase";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
  query,
  where,
  getDocs,
} from "firebase/firestore";

/* ---------- Canonical Services ---------- */
export const serviceItemsCol = collection(db, "services", "servicelist", "data");

/* ---------- Categories ---------- */
export const categoriesCol = collection(db, "services", "servicelist", "categories");

/* ---------- NEW: Subcategories under same root ---------- */
export const subcategoriesCol = collection(db, "services", "servicelist", "subcategories");

/* ---------- NEW: Bookings collection (centralized) ---------- */
export const bookingsCol = collection(db, "services", "bookings", "bookinglist");

export const rolesConfigCol = collection(db, "admin", "config", "roles");

/** Subscribe to all categories (client-side sort by label) */
export const subscribeCategories = (onData, onError) => {
  return onSnapshot(
    categoriesCol,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
      onData(items);
    },
    (err) => onError?.(err)
  );
};

/** Create category */
export const createCategory = async ({ key, label, active = true }) => {
  if (!key) throw new Error("Category key is required");
  const payload = {
    key,
    label: label || key,
    active: !!active,
    createdBy: auth?.currentUser?.uid || "unknown",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  return await addDoc(categoriesCol, payload);
};

/** Update category */
export const updateCategory = async (id, updates) => {
  const ref = doc(categoriesCol, id);
  const payload = {
    ...updates,
    updatedAt: serverTimestamp(),
  };
  await updateDoc(ref, payload);
};

/** Toggle category active */
export const setCategoryActive = async (id, active) => {
  const ref = doc(categoriesCol, id);
  await updateDoc(ref, { active: !!active, updatedAt: serverTimestamp() });
};

/** Delete category */
export const deleteCategory = async (id) => {
  const ref = doc(categoriesCol, id);
  await deleteDoc(ref);
};

/* ---------- NEW: Subcategories APIs ---------- */

/** Subscribe to all subcategories OR only for a categoryKey */
export const subscribeSubcategories = (onData, onError, { categoryKey } = {}) => {
  const q = categoryKey
    ? query(subcategoriesCol, where("categoryKey", "==", categoryKey))
    : subcategoriesCol;

  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => (a.label || "").localeCompare(b.label || ""));
      onData(items);
    },
    (err) => onError?.(err)
  );
};

/** Create subcategory (requires parent categoryKey) */
export const createSubcategory = async ({ key, label, categoryKey, active = true }) => {
  if (!key) throw new Error("Subcategory key is required");
  if (!categoryKey) throw new Error("Parent category is required");
  const payload = {
    key,
    label: label || key,
    categoryKey,
    active: !!active,
    createdBy: auth?.currentUser?.uid || "unknown",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  return await addDoc(subcategoriesCol, payload);
};

/** Update subcategory */
export const updateSubcategory = async (id, updates = {}) => {
  const ref = doc(subcategoriesCol, id);
  const payload = { ...updates, updatedAt: serverTimestamp() };
  await updateDoc(ref, payload);
};

/** Toggle subcategory active */
export const setSubcategoryActive = async (id, active) => {
  const ref = doc(subcategoriesCol, id);
  await updateDoc(ref, { active: !!active, updatedAt: serverTimestamp() });
};

/** Delete subcategory */
export const deleteSubcategory = async (id) => {
  const ref = doc(subcategoriesCol, id);
  await deleteDoc(ref);
};

/* ---------- Services: subscribe/list/create/update/toggle/delete ---------- */
/**
 * Subscribe to services
 * Filters: { category, subcategory }
 */
export const subscribeServiceItems = (onData, onError, { category, subcategory } = {}) => {
  let qRef = serviceItemsCol;

  if (category && category !== "all") {
    qRef = query(qRef, where("category", "==", category));
  }
  if (subcategory && subcategory !== "all") {
    qRef = query(qRef, where("subcategory", "==", subcategory));
  }

  return onSnapshot(
    qRef,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      onData(items);
    },
    (err) => onError?.(err)
  );
};

/** Create service from Admin form → canonical schema */
export const createServiceFromAdminForm = async ({
  category,
  subcategory, // NEW
  type,        // slug (optional)
  title,       // name
  desc,        // description
  icon,        // optional
  price,
  active = true,
  formFields,
}) => {
  if (!category || !title || price == null || price === "") {
    throw new Error("Please fill category, title and price.");
  }

  const createdBy = auth?.currentUser?.uid || "unknown";
  const numPrice = typeof price === "number" ? price : parseFloat(price);

  const payload = {
    // uid: undefined, // will set after addDoc
    name: title,
    description: desc || "",
    category,
    ...(subcategory ? { subcategory } : {}),
    price: Number.isFinite(numPrice) ? numPrice : 0,
    isActive: !!active,
    createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    icon: icon || "bi-tools",
    ...(type ? { slug: type } : {}),
    ...(Array.isArray(formFields) ? { formFields } : {}),
  };

  const ref = await addDoc(serviceItemsCol, payload);
  await updateDoc(ref, { uid: ref.id, updatedAt: serverTimestamp() });
  return ref;
};

/** Update service (full/partial) */
export const updateServiceItem = async (id, updates = {}) => {
  const ref = doc(serviceItemsCol, id);

  const mapped = { ...updates };

  // Allow 'title' → 'name'
  if (mapped.title && !mapped.name) {
    mapped.name = mapped.title;
    delete mapped.title;
  }

  // Normalize price
  if (mapped.price != null) {
    const p = typeof mapped.price === "number" ? mapped.price : parseFloat(mapped.price);
    mapped.price = Number.isFinite(p) ? p : 0;
  }

  // Remove any undefined keys (Firestore rejects them)
  Object.keys(mapped).forEach((k) => {
    if (mapped[k] === undefined) delete mapped[k];
  });

  mapped.updatedAt = serverTimestamp();
  await updateDoc(ref, mapped);
};

/** Toggle isActive */
export const setServiceItemActive = async (id, isActive) => {
  const ref = doc(serviceItemsCol, id);
  await updateDoc(ref, { isActive: !!isActive, updatedAt: serverTimestamp() });
};

/** Delete service */
export const deleteServiceItem = async (id) => {
  const ref = doc(serviceItemsCol, id);
  await deleteDoc(ref);
};

/** List only active services (optional filters) */
export const listActiveServices = async ({ category, subcategory } = {}) => {
  let qRef = serviceItemsCol;

  if (category && category !== "all") {
    qRef = query(qRef, where("category", "==", category));
  }
  if (subcategory && subcategory !== "all") {
    qRef = query(qRef, where("subcategory", "==", subcategory));
  }

  const snap = await getDocs(qRef);
  const items = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((s) => !!s.isActive);

  items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  return items;
};


/* ---------- NEW: Create a booking ---------- */
/**
 * Create a booking in central collection and optionally mirror under the service doc.
 * @param {Object} service - the selected service document (should include id or uid)
 * @param {Object} booking - collected data { date, time, address, notes, formData }
 * @param {Object} options - { mirrorUnderServiceDoc: boolean }
 */

// src/lib/servicesApi.js


export const createBooking = async (service, booking, options = { mirrorUnderServiceDoc: true }) => {
  if (!service?.id && !service?.uid) {
    throw new Error("Missing service id/uid in booking.");
  }

  const requestedBy = auth?.currentUser?.uid || "anonymous";
  const serviceId = service.id || service.uid;

  const serviceSnapshot = {
    serviceUid: service.uid || service.id,
    serviceId: service.id || service.uid,
    serviceName: service.name || service.title || "Untitled service",
    category: service.category,
    subcategory: service.subcategory,
    price: service.price,
    icon: service.icon || "bi-tools",
  };

  const mobileSanitized = (booking?.mobile || "").toString().replace(/\D/g, "") || null;

  const payload = {
    ...serviceSnapshot,
    status: "open",
    requestedBy,
    requestedAt: serverTimestamp(),
    updatedAt: serverTimestamp(), // ensure create passes rules that may require updatedAt
    ...(booking?.date ? { date: booking.date } : {}),
    ...(booking?.time ? { time: booking.time } : {}),
    ...(booking?.address ? { address: booking.address } : {}),
    ...(booking?.notes ? { notes: booking.notes } : {}),
    ...(mobileSanitized ? { mobile: mobileSanitized } : {}),
    formData: booking?.formData || {},
  };

  // --- Debug logs: path + keys help pinpoint rule failures ---
  // if (process?.env?.NODE_ENV !== "production") {
  //   console.log("[createBooking] central path:", "services/servicelist/bookings/*");
  //   console.log("[createBooking] payload keys:", Object.keys(payload));
  // }

  console.log("[createBooking] central path:", "services/servicelist/bookings/*");
  console.log("[createBooking] payload keys:", Object.keys(payload));
  console.log("[createBooking] mirror path:", `services/servicelist/data/${serviceId}/bookings/*`);

  // Central booking (authoritative)
  const bookingRef = await addDoc(bookingsCol, payload);
  await updateDoc(bookingRef, { uid: bookingRef.id });



  return bookingRef;
};


/* ---------- NEW: Subscribe to bookings for a given service ---------- */
export const subscribeBookingsByService = (serviceId, onData, onError) => {
  if (!serviceId) throw new Error("serviceId is required");
  const qRef = query(bookingsCol, where("serviceId", "==", serviceId));
  return onSnapshot(
    qRef,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => (a.requestedAt?.seconds || 0) - (b.requestedAt?.seconds || 0));
      onData(items);
    },
    (err) => onError?.(err)
  );
};

/* ---------- NEW: Update booking status ---------- */
export const updateBookingStatus = async (bookingUid, status) => {
  const ref = doc(bookingsCol, bookingUid);
  await updateDoc(ref, { status, updatedAt: serverTimestamp() });
};

/* ---------- NEW: Delete booking (central + mirror best-effort) ---------- */
export const deleteBooking = async (bookingUid, serviceId) => {
  const ref = doc(bookingsCol, bookingUid);
  await deleteDoc(ref);

  if (serviceId) {
    try {
      const svcBookingsCol = collection(db, "services", "servicelist", "data", serviceId, "bookings");
      const qs = await getDocs(query(svcBookingsCol, where("bookingUid", "==", bookingUid)));
      await Promise.all(qs.docs.map((d) => deleteDoc(doc(svcBookingsCol, d.id))));
    } catch (err) {
      console.warn("Mirror deletion failed:", err?.code || err?.message || err);
    }
  }
};


/* ---------- NEW: Subscribe to ALL bookings (admin view) ---------- */
export const subscribeAllBookings = (onData, onError, { category } = {}) => {
  try {
    let qRef = bookingsCol;
    if (category && category !== "all") {
      qRef = query(qRef, where("category", "==", category));
    }
    return onSnapshot(
      qRef,
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Sort newest first
        items.sort((a, b) => (b.requestedAt?.seconds || 0) - (a.requestedAt?.seconds || 0));
        onData(items);
      },
      (err) => onError?.(err)
    );
  } catch (e) {
    onError?.(e);
    return () => { };
  }
};

/* ---------- NEW: Users / Roles config (admin UI) ---------- */
export const subscribeUsers = (onData, onError) => {
  try {
    const usersCol = collection(db, "users");
    return onSnapshot(
      usersCol,
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        onData(items);
      },
      (err) => onError?.(err)
    );
  } catch (e) {
    onError?.(e);
    return () => { };
  }
};

export const updateUserRole = async (uid, { role, updatedByUid }) => {
  if (!uid || !role) throw new Error("uid and role are required");
  const userRef = doc(db, "users", uid);
  await updateDoc(userRef, {
    role,
    updatedAt: serverTimestamp(),
    updatedBy: updatedByUid || auth?.currentUser?.uid || "admin",
  });
};

/* ---------- NEW: Roles config storage (admin-only) ---------- */

export const subscribeRoleConfigs = (onData, onError) => {
  return onSnapshot(
    rolesConfigCol,
    (snap) => {
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      onData(items);
    },
    (err) => onError?.(err)
  );
};

export const upsertRoleConfig = async (roleId, permissions = { create: false, read: true, update: false, delete: false }) => {
  if (!roleId) throw new Error("roleId is required");
  const ref = doc(rolesConfigCol, roleId);
  try {
    await updateDoc(ref, { ...permissions, updatedAt: serverTimestamp() });
  } catch {
    await addDoc(rolesConfigCol, { roleId, ...permissions, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  }
};


